import { type RefObject, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useSearchParams } from "react-router-dom";

import { sendTelemetryEvent } from "@/lib/telemetry";
import {
    FlyerDisplayPosition,
    type IFlyerCatalogueImage,
} from "@/src/flyers/FlyerCatalogue/FlyerCatalogueHelpers";
import { FlyerCataloguePageAd } from "@/src/flyers/FlyerCatalogue/FlyerCataloguePageAd";
import { useFlyerDetailsContext } from "@/src/flyers/FlyerDetails/FlyerDetailsContext";
import ImageLoaderBlur from "@Components/ImageLoader/ImageLoaderBlur";
import { onDefaultKeyboardUp } from "@Components/Modules/Helpers";
import { flyerPageQueryParam } from "@Constants/PortalConstants";
import type { QuerySmMax, QueryXsMax } from "@stylex/tokens.stylex";
import * as stylex from "@stylexjs/stylex";

export type IFlyerCataloguePage = {
    image: IFlyerCatalogueImage;
    flyerId: string;
    isMobileOrTablet: boolean;
    carouselRef?: RefObject<HTMLOListElement>;
    onVisible?: (carouselIndex: number) => void;
};

const renderBanner = ({ image, flyerId }: IFlyerCataloguePage, setHide: (value: boolean) => void) => {
    if (image.adContent?.adUnitId) {
        return (
            <FlyerCataloguePageAd
                {...image}
                flyerId={flyerId}
                key={image.carouselPosition}
                styleX={styles.centerItem}
                onSlotRenderFail={() => setHide(true)}
            />
        );
    }

    return (
        <a
            key={image.carouselPosition}
            {...stylex.props(styles.button)}
            href={image.bannerUrl}
            target="_blank"
            rel="noreferrer"
            onClick={() => {
                if (image.bannerUrl) {
                    sendTelemetryEvent({
                        eventName: "CustomEvent-flyer-pageBanner",
                        actionType: "Click",
                        itemId: flyerId,
                        param1: image.bannerId,
                    });
                }
            }}
        >
            <ImageLoaderBlur {...image.image} sizeDirection="width" size={600} />
        </a>
    );
};

export const FlyerCataloguePage = (props: IFlyerCataloguePage) => {
    const [nodeRef] = useInView({
        root: props.carouselRef?.current,
        rootMargin: "-50% -50% -50% -50%",
        onChange: (inView) => {
            if (!inView) return;

            props.onVisible(props.image.carouselPosition);
        },
    });
    const { setIsZoomed, isZoomed } = useFlyerDetailsContext();
    const [searchParams, setSearchParams] = useSearchParams();
    const [shouldHide, setShouldHide] = useState(false);

    if (!props.image || shouldHide) {
        return <li {...stylex.props(styles.hide)} />;
    }

    const isCenter = props.isMobileOrTablet || props.image.displayPosition === FlyerDisplayPosition.Center;

    const positionCss = isCenter
        ? styles.centerItem
        : props.image.displayPosition === FlyerDisplayPosition.Left
          ? styles.snapLeft
          : styles.snapRight;

    const imageCss =
        isCenter && !isZoomed
            ? undefined
            : props.image.displayPosition === FlyerDisplayPosition.Left
              ? styles.imageLeft
              : styles.imageRight;

    return (
        <li
            {...stylex.props(styles.flyerCataloguePageComponent, positionCss, isZoomed && styles.makeZoom)}
            ref={nodeRef}
        >
            {props.image.isBanner ? (
                renderBanner(props, setShouldHide)
            ) : (
                <button
                    key={props.image.carouselPosition}
                    {...stylex.props(styles.button, isZoomed ? styles.customZoomOut : styles.customZoomIn)}
                    onClick={(ev) => {
                        if (props.isMobileOrTablet === false) {
                            searchParams.set(flyerPageQueryParam, `${props.image.pageNumber}`);
                            setSearchParams(searchParams, { replace: true });
                            const parentElement = ev.currentTarget.parentElement;
                            parentElement.scrollIntoView();
                            setIsZoomed(!isZoomed);
                        }
                    }}
                    onKeyUp={onDefaultKeyboardUp}
                    tabIndex={0}
                >
                    <ImageLoaderBlur
                        {...props.image.image}
                        styleX={isZoomed ? styles.imageZoom : imageCss}
                        sizeDirection="width"
                        size={1200}
                    />
                </button>
            )}
        </li>
    );
};

const smMax: QuerySmMax = "@media (max-width: 989px)";
const xsMax: QueryXsMax = "@media (max-width: 759px)";

const styles = stylex.create({
    flyerCataloguePageComponent: {
        flex: "0 0 100%",
        justifyContent: "center",
        height: "100%",
    },

    snapLeft: {
        flexBasis: "50%",
        scrollSnapAlign: "start",
    },
    snapRight: {
        flexBasis: "50%",
        scrollSnapAlign: "end",
    },

    centerItem: {
        padding: {
            default: "0 25%",
            [smMax]: 0,
        },
        flexBasis: {
            default: "100%",
            [xsMax]: "90%",
        },
        scrollSnapAlign: "center",
        textAlign: "center",
    },

    button: {
        height: "100%",
        width: "100%",
    },

    makeZoom: {
        padding: 0,
        flexBasis: "100%",
        scrollbarWidth: "none",
        touchAction: "manipulation",
        zIndex: 100,
        overflowY: "scroll",
    },

    imageLeft: {
        objectPosition: "right",
    },
    imageRight: {
        objectPosition: "left",
    },

    imageZoom: {
        height: "auto",
        marginBottom: 0,
        marginLeft: "auto",
        marginRight: "auto",
        marginTop: 0,
    },

    customZoomIn: {
        cursor: "url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHZpZXdCb3g9JzAgMCAyNCAyNCcgZmlsbD0nbm9uZScgc3Ryb2tlPScjZmZmJyBzdHJva2Utd2lkdGg9JzInID48Y2lyY2xlIGN4PScxMScgY3k9JzExJyByPSc4Jy8+PGxpbmUgeDE9JzIxJyB4Mj0nMTYuNjUnIHkxPScyMScgeTI9JzE2LjY1Jy8+PGxpbmUgeDE9JzExJyB4Mj0nMTEnIHkxPSc4JyB5Mj0nMTQnLz48bGluZSB4MT0nOCcgeDI9JzE0JyB5MT0nMTEnIHkyPScxMScvPjwvc3ZnPg) 24 24, zoom-in",
    },

    customZoomOut: {
        cursor: `url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHZpZXdCb3g9JzAgMCAyNCAyNCcgZmlsbD0nbm9uZScgc3Ryb2tlPScjZmZmJyBzdHJva2Utd2lkdGg9JzInPjxjaXJjbGUgY3g9JzExJyBjeT0nMTEnIHI9JzgnLz48bGluZSB4MT0nMjEnIHgyPScxNi42NScgeTE9JzIxJyB5Mj0nMTYuNjUnLz48bGluZSB4MT0nOCcgeDI9JzE0JyB5MT0nMTEnIHkyPScxMScvPjwvc3ZnPg) 24 24, zoom-out`,
    },
    hide: {
        display: "none",
    },
});
